/**
 * What a draft Item Return holds for "Back into United stock" (D44 #8, #36-#38,
 * #42, VR-4): for one credit note line, how many of each batch's own unit (or
 * which weighed boxes) go back into each batch the line shipped from.
 *
 * Pure, no I/O. The server owns the rule (services/creditNoteStockReturn.service.js
 * `planStockReturn`) and checks every figure again on Issue; this only holds what
 * was typed and ticked, fills the one obvious figure in, and lets the form show the
 * same check before the person presses Issue. No money is worked out here.
 */
import type { ReturnSource, ReturnSourceLine, ReturnSources, StockReturnEntry } from '../../lib/creditNoteApi';
import { roundQty } from '../../utils/invoiceLineMath';

/** One source as typed: the batch quantity exactly as in the box, or the boxes ticked. */
export type SourceEntryDraft = { quantity: string; caseSeqs: number[] };

export type RowSplit = {
  /** By source key, `${shipmentMovementId}:${layerId}`. */
  entries: Record<string, SourceEntryDraft>;
  /** The person typed a batch quantity on this row: the filled-in figure stops following the credit. */
  handEdited: boolean;
  /** The credited quantity the row was saved with, when it was read back with a split. */
  savedCredited: number | null;
};

export const EMPTY_SPLIT: RowSplit = { entries: {}, handEdited: false, savedCredited: null };

export function sourceKeyOf(entry: { shipmentMovementId: string; layerId: string }): string {
  return `${entry.shipmentMovementId}:${entry.layerId}`;
}

/** A saved line's split, as the form holds it. */
export function splitFromSaved(entries: StockReturnEntry[] | undefined, credited: number): RowSplit {
  const list = Array.isArray(entries) ? entries : [];
  if (!list.length) return EMPTY_SPLIT;
  const out: Record<string, SourceEntryDraft> = {};
  list.forEach((e) => {
    out[sourceKeyOf(e)] = {
      quantity: e.caseSeqs?.length ? '' : String(e.qtyLevel1),
      caseSeqs: [...(e.caseSeqs || [])].sort((a, b) => a - b),
    };
  });
  return { entries: out, handEdited: false, savedCredited: roundQty(Math.abs(credited)) };
}

/**
 * A credit note line's sources, or null when they have not been read. A line the
 * read does not list has none: the server refuses it with CN_RETURN_NO_BATCH.
 */
export function sourceLineOf(
  sources: ReturnSources | null | undefined,
  lineNumber: number | null | undefined
): ReturnSourceLine | null {
  if (!sources || lineNumber === null || lineNumber === undefined) return null;
  return (
    sources.lines.find((l) => l.lineNumber === lineNumber) ?? {
      lineNumber,
      itemName: '',
      unit: '',
      quantity: 0,
      sources: [],
    }
  );
}

/**
 * The one source a line's batch quantity fills itself in for (#36): the line has
 * exactly one, it is not weighed boxes, and its unit converts from the client's.
 */
export function autoFillSource(line: ReturnSourceLine | null | undefined): ReturnSource | null {
  if (!line || line.sources.length !== 1) return null;
  const only = line.sources[0];
  return !only.boxes && only.level1PerClientUnit ? only : null;
}

function filledQuantity(source: ReturnSource, credited: number): number {
  return roundQty(Math.abs(credited) * (source.level1PerClientUnit || 0));
}

/** Whether the filled-in figure still follows the credited quantity. */
function followsCredited(split: RowSplit, source: ReturnSource): boolean {
  if (split.handEdited) return false;
  const saved = split.entries[source.key];
  // Read back from a saved draft: it follows only if it was the filled-in figure
  // when it was saved. A figure typed by hand before saving stays as typed.
  if (saved && split.savedCredited !== null) {
    return saved.quantity.trim() !== '' && Number(saved.quantity) === filledQuantity(source, split.savedCredited);
  }
  return true;
}

/** Whether a source's quantity box shows the filled-in figure rather than a typed one. */
export function isAutoFilled(split: RowSplit, source: ReturnSource, line: ReturnSourceLine | null): boolean {
  const auto = autoFillSource(line);
  return Boolean(auto && auto.key === source.key && followsCredited(split, auto));
}

/** What the batch quantity box for a source shows. */
export function quantityTextOf(
  split: RowSplit,
  source: ReturnSource,
  line: ReturnSourceLine | null,
  credited: number
): string {
  if (isAutoFilled(split, source, line)) return String(filledQuantity(source, credited));
  return split.entries[source.key]?.quantity ?? '';
}

/** The boxes ticked on a source, only those it still lists (a box not listed cannot go back). */
export function tickedBoxesOf(split: RowSplit, source: ReturnSource): number[] {
  const listed = new Set((source.boxes || []).map((b) => b.seq));
  return (split.entries[source.key]?.caseSeqs || []).filter((seq) => listed.has(seq)).sort((a, b) => a - b);
}

/** The weight of the boxes ticked on a source, in its unit. A quantity, never money. */
export function tickedWeightOf(split: RowSplit, source: ReturnSource): number {
  const seqs = new Set(tickedBoxesOf(split, source));
  return roundQty((source.boxes || []).reduce((sum, box) => (seqs.has(box.seq) ? sum + box.weightLevel1 : sum), 0));
}

/**
 * What a row sends as `stockReturn`: each source with something entered, in the
 * server's shape. A box source sends its boxes and quantity 0; the server weighs
 * them. Before the sources are read, what was saved or typed goes as it is.
 */
export function stockReturnPayloadOf(
  split: RowSplit | undefined,
  line: ReturnSourceLine | null,
  credited: number
): StockReturnEntry[] {
  const draft = split ?? EMPTY_SPLIT;
  if (!line) {
    return Object.entries(draft.entries).flatMap(([key, e]) => {
      const [shipmentMovementId, layerId] = key.split(':');
      if (e.caseSeqs.length) return [{ shipmentMovementId, layerId, qtyLevel1: 0, caseSeqs: [...e.caseSeqs] }];
      const text = e.quantity.trim();
      if (text === '' || Number(text) === 0) return [];
      return [{ shipmentMovementId, layerId, qtyLevel1: Number(text), caseSeqs: [] }];
    });
  }
  return line.sources.flatMap((source) => {
    const base = { shipmentMovementId: source.shipmentMovementId, layerId: source.layerId };
    if (source.boxes) {
      const caseSeqs = tickedBoxesOf(draft, source);
      return caseSeqs.length ? [{ ...base, qtyLevel1: 0, caseSeqs }] : [];
    }
    const text = quantityTextOf(draft, source, line, credited).trim();
    // An empty box goes as nothing, never as 0; a figure the server cannot take
    // goes as typed, and it names the line.
    if (text === '' || Number(text) === 0) return [];
    return [{ ...base, qtyLevel1: Number(text), caseSeqs: [] }];
  });
}

export interface ReturnCheck {
  credited: number;
  /** In the client's unit; null when a chosen batch's unit does not convert (#42). */
  goingBack: number | null;
  mismatch: boolean;
}

/**
 * "5 CASE credited · 5 CASE going back": the check the server makes on Issue
 * (CN_RETURN_QUANTITY_MISMATCH), shown while typing. null when there are no sources.
 */
export function returnCheckOf(split: RowSplit | undefined, line: ReturnSourceLine | null, credited: number): ReturnCheck | null {
  if (!line || !line.sources.length) return null;
  const draft = split ?? EMPTY_SPLIT;
  const wanted = roundQty(Math.abs(credited));
  let total = 0;
  for (const source of line.sources) {
    let qtyLevel1 = 0;
    if (source.boxes) {
      qtyLevel1 = tickedWeightOf(draft, source);
    } else {
      const n = Number(quantityTextOf(draft, source, line, credited).trim() || '0');
      qtyLevel1 = Number.isFinite(n) && n > 0 ? n : 0;
    }
    if (!(qtyLevel1 > 0)) continue;
    if (!source.level1PerClientUnit) return { credited: wanted, goingBack: null, mismatch: false };
    total += qtyLevel1 / source.level1PerClientUnit;
  }
  const goingBack = roundQty(total);
  return { credited: wanted, goingBack, mismatch: goingBack !== wanted };
}

/** The line's own message when nothing it sent can be found in a batch (the server's CN_RETURN_NO_BATCH). */
export function noSourcesMessage(vesselName: string): string {
  return `Nothing this line sent to ${vesselName || 'the vessel'} can be found in a batch, so its goods cannot go back into United stock. Put this line on its own credit note with another answer.`;
}
